import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'
import {observeStickyHeaderHeight, unobserveStickyHeaderHeight} from '@github-ui/sticky-css'

interface UseViewportFitHeightOptions {
  /**
   * When false, the height is computed once for the element's final sticky
   * position (ignoring the global header) and only recalculated on window
   * resize.  The panel may extend below the viewport while the header is
   * visible, but stays stable as the user scrolls.
   *
   * @default true
   */
  recalculateOnScroll?: boolean

  /**
   * Pixels reserved at the bottom of the fitted region.
   * Useful when content includes persistent bottom padding/chrome that must
   * remain visible on initial render.
   *
   * @default 0
   */
  bottomInsetPx?: number

  /**
   * Enables viewport fitting behavior.
   * When false, no listeners are attached and style updates are skipped.
   *
   * @default true
   */
  enabled?: boolean

  /**
   * An element that acts as the lower boundary of the fitted region.
   * When this element scrolls into the viewport (e.g. a page footer), the
   * available height is clamped to stop at the boundary element's top edge
   * rather than the viewport bottom.  When the boundary is off-screen or
   * hidden the behaviour is identical to not providing this option.
   */
  bottomBoundaryRef?: RefObject<Element | null>

  /**
   * When true, also clamps the `maxHeight` of the nearest sticky ancestor
   * (e.g. a sidebar wrapper with `height: 100vh`), preventing the browser
   * from displacing that sticky wrapper upward when a lower boundary scrolls
   * into view.  Only needed when the element lives inside a sticky wrapper
   * that the consumer cannot constrain directly.
   *
   * @default false
   */
  clampStickyAncestor?: boolean
}

/**
 * Returns the nearest ancestor that is actually `position: sticky`, or null.
 *
 * Primer's PageLayout marks its sticky wrappers with `data-sticky`, but
 * drops sticky positioning at narrow viewports while keeping `data-sticky`,
 * so confirm sticky-ness with a `getComputedStyle` check.
 */
function findStickyAncestor(el: HTMLElement): HTMLElement | null {
  const candidate = el.parentElement?.closest<HTMLElement>('[data-sticky]') ?? null
  return candidate && getComputedStyle(candidate).position === 'sticky' ? candidate : null
}

/**
 * Dynamically sets `maxHeight` on the element so it never extends below
 * the viewport bottom.  Recalculates on scroll and resize via
 * requestAnimationFrame to avoid layout thrashing.
 *
 * When `recalculateOnScroll` is false the height is sized for the element's
 * final sticky position (header scrolled away) and only updates on resize,
 * keeping the panel stable while the user scrolls the global header.
 *
 * When `clampStickyAncestor` is true the hook also finds the nearest sticky
 * ancestor (e.g. a sidebar wrapper with `height: 100vh`) and applies
 * `maxHeight` to it as well, preventing the browser from displacing the sticky
 * element upward when a footer or other lower boundary scrolls into view.
 */
export function useViewportFitHeight(ref: RefObject<HTMLElement | null>, options?: UseViewportFitHeightOptions) {
  const rafRef = useRef(0)
  const recalculateOnScroll = options?.recalculateOnScroll ?? true
  const bottomInsetPx = options?.bottomInsetPx ?? 0
  const enabled = options?.enabled ?? true
  const bottomBoundaryRef = options?.bottomBoundaryRef
  const clampStickyAncestor = options?.clampStickyAncestor ?? false
  const hasHadScrollOrResizeRef = useRef(false)
  const insetPxRef = useRef(bottomInsetPx)

  useEffect(() => {
    if (!enabled) return

    const el = ref.current
    if (!el) return

    // Reset on every effect run (when options change)
    hasHadScrollOrResizeRef.current = false
    insetPxRef.current = bottomInsetPx

    // Capture the sticky ancestor once per effect run.  Applying maxHeight
    // to it prevents the browser from displacing a sticky wrapper with
    // `height: 100vh` when a footer scrolls into view.
    //
    // Read its sticky `top` offset not `getBoundingClientRect().top`. When the
    // browser displaces the wrapper upward, its `height: 100vh` overflows the
    // containing block, `.top` goes negative and `viewportBottom - .top` would
    // exceed 100vh. CSS `top` is the intended sticky position regardless of
    // displacement. It is static except when it references
    // `--base-sticky-header-height` (the notification shelf), so it is refreshed
    // via `observeStickyHeaderHeight` below rather than re-read on every frame.
    const stickyAncestor = clampStickyAncestor ? findStickyAncestor(el) : null
    let stickyTopOffset = stickyAncestor ? parseFloat(getComputedStyle(stickyAncestor).top) || 0 : 0

    // Cache last value written to each style property so we can skip no-op writes.
    let lastMaxHeight: string | undefined
    let lastPanelHeight: string | undefined
    let lastStickyMaxHeight: string | undefined

    // Used to gate the per-frame bottomBoundary read. Initialized to `true` so the first
    // synchronous measure is correct before the observer's async callback arrives.
    let bottomBoundaryVisible = true

    // When recalculateOnScroll is false we compute the element's offset
    // within its sticky sidebar so the height matches the final position
    // (header scrolled away) rather than the current viewport position.
    function getOffsetInStickyParent(): number {
      let offset = 0
      let current: HTMLElement | null = el
      while (current) {
        offset += current.offsetTop
        const parent = current.offsetParent as HTMLElement | null
        if (!parent || parent === document.body) break
        const position = getComputedStyle(parent).position
        if (position === 'sticky') {
          // Found the sticky ancestor — offset is now relative to it
          break
        }
        current = parent
      }
      return offset
    }

    function measure() {
      rafRef.current = 0
      if (!el) return

      // Apply inset only before any scroll/resize events occur
      const currentInset = hasHadScrollOrResizeRef.current ? 0 : insetPxRef.current

      // If there's a bottomBoundaryRef, clamp the bottom boundary to the boundary
      // element's top when it is visible within the viewport (e.g. a page footer
      // scrolling into view).
      const bottomBoundaryEl = bottomBoundaryRef?.current
      let viewportBottom = window.innerHeight
      if (bottomBoundaryEl && bottomBoundaryVisible) {
        const boundaryTop = bottomBoundaryEl.getBoundingClientRect().top
        if (boundaryTop > 0 && boundaryTop < window.innerHeight) {
          viewportBottom = boundaryTop
        }
      }

      let available: number
      if (recalculateOnScroll) {
        available = Math.max(0, viewportBottom - el.getBoundingClientRect().top)
      } else {
        // Size for the final sticky position so the panel is stable
        const offsetInSidebar = getOffsetInStickyParent()
        available = Math.max(0, viewportBottom - offsetInSidebar)
      }
      const adjustedAvailable = Math.max(0, available - currentInset)
      const maxHeight = `${adjustedAvailable}px`
      if (maxHeight !== lastMaxHeight) {
        el.style.maxHeight = maxHeight
        lastMaxHeight = maxHeight
      }
      // Expose available height so descendant overlay constraints (e.g.
      // embeddedMenuPortalContainer) can size themselves to the panel.
      // Subtract extra space to account for the anchor element and padding
      // so overlays fit above the anchor without Primer flipping direction.
      const panelHeight = `${Math.max(0, adjustedAvailable - 30)}px`
      if (panelHeight !== lastPanelHeight) {
        el.style.setProperty('--panel-height', panelHeight)
        lastPanelHeight = panelHeight
      }

      // Shrink the sticky ancestor's fixed `height: 100vh` to stop at the same
      // lower boundary, using the cached sticky `top` offset.
      if (stickyAncestor) {
        const stickyMaxHeight = `${Math.max(0, viewportBottom - stickyTopOffset)}px`
        if (stickyMaxHeight !== lastStickyMaxHeight) {
          stickyAncestor.style.maxHeight = stickyMaxHeight
          lastStickyMaxHeight = stickyMaxHeight
        }
      }
    }

    function scheduleUpdate() {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(measure)
    }

    // Mark that a scroll/resize event has occurred
    function onScrollOrResize() {
      hasHadScrollOrResizeRef.current = true
      scheduleUpdate()
    }

    // Initial measurement
    measure()

    // Re-measure once after paint so sticky/layout state has settled.
    // This avoids first-open clipping where initial sync measurement can
    // run before the panel reaches its final position.
    scheduleUpdate()

    // The sticky ancestor's CSS `top` may reference --base-sticky-header-height,
    // which changes when the notification shelf is shown or dismissed.  Refresh
    // the cached offset and re-measure on those (rare) changes so the clamp
    // tracks the shelf without paying a getComputedStyle cost on every frame.
    let unobserveStickyHeader: (() => void) | undefined
    if (stickyAncestor) {
      const handleStickyHeaderChange = () => {
        stickyTopOffset = parseFloat(getComputedStyle(stickyAncestor).top) || 0
        scheduleUpdate()
      }
      observeStickyHeaderHeight(handleStickyHeaderChange)
      unobserveStickyHeader = () => unobserveStickyHeaderHeight(handleStickyHeaderChange)
    }

    // An IntersectionObserver cannot drive the *panel* clamp itself: setting
    // maxHeight on the observed element changes its intersection, creating a
    // feedback loop.  Passive scroll + rAF is the simplest correct approach for
    // viewport-clamping the panel.  We can, however, observe the *bottom boundary* to
    // cheaply gate the per-frame bottomBoundary read above.
    let bottomBoundaryObserver: IntersectionObserver | undefined
    const bottomBoundaryEl = bottomBoundaryRef?.current
    if (bottomBoundaryEl && typeof IntersectionObserver !== 'undefined') {
      bottomBoundaryObserver = new IntersectionObserver(
        ([entry]) => {
          bottomBoundaryVisible = entry?.isIntersecting ?? true
          scheduleUpdate()
        },
        // Small bottom margin so the bottom boundary registers as visible a few pixels
        // before it enters, resuming per-frame reads without a visible jump.
        {rootMargin: '0px 0px 10px 0px'},
      )
      bottomBoundaryObserver.observe(bottomBoundaryEl)
    }

    if (recalculateOnScroll) {
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('scroll', onScrollOrResize)
    }
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      if (recalculateOnScroll) {
        window.removeEventListener('scroll', onScrollOrResize)
      }
      window.removeEventListener('resize', onScrollOrResize)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      unobserveStickyHeader?.()
      bottomBoundaryObserver?.disconnect()
      el.style.maxHeight = ''
      el.style.removeProperty('--panel-height')
      if (stickyAncestor) stickyAncestor.style.maxHeight = ''
    }
  }, [ref, recalculateOnScroll, bottomInsetPx, enabled, bottomBoundaryRef, clampStickyAncestor])
}
